import { useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { useI18n } from "@/context/Shop";
import { formatALL } from "@/lib/format";

const csv = (v) => (v ? v.split(",").filter(Boolean) : []);
const slug = (s) => String(s).replace(/[^a-z0-9]+/gi, "-").toLowerCase();

function CheckRow({ id, checked, onChange, label, count, testid }) {
  return (
    <label htmlFor={id} className="flex items-center gap-3 py-1.5 text-sm cursor-pointer" data-testid={testid}>
      <Checkbox id={id} checked={checked} onCheckedChange={onChange} className="rounded-none" />
      <span className="flex-1">{label}</span>
      {count !== undefined && <span className="text-xs text-muted-foreground tabular">{count}</span>}
    </label>
  );
}

export function FilterPanel({ facets, params, setParam, hide = [] }) {
  const { t, tr } = useI18n();
  const [pmin, setPmin] = useState(params.get("min_price") || "");
  const [pmax, setPmax] = useState(params.get("max_price") || "");
  if (!facets) return null;
  const toggle = (key, value) => {
    const cur = csv(params.get(key));
    const next = cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value];
    setParam(key, next.join(","));
  };
  const genders = (facets.genders || []).filter((g) => g.value && g.value !== "unisex");
  const sections = [
    !hide.includes("gender") && genders.length > 1 && { key: "gender", title: t("audience"), body: genders.map((g) => (
      <CheckRow key={g.value} id={`f-gender-${g.value}`} checked={params.get("gender") === g.value} onChange={() => setParam("gender", params.get("gender") === g.value ? "" : g.value)} label={t(g.value)} count={g.count} testid={`filter-gender-${g.value}`} />
    )) },
    !hide.includes("category") && facets.categories.length > 1 && { key: "category", title: t("category"), body: facets.categories.map((c) => (
      <CheckRow key={c.slug} id={`f-cat-${c.slug}`} checked={params.get("category") === c.slug} onChange={() => setParam("category", params.get("category") === c.slug ? "" : c.slug)} label={tr(c)} count={c.count} testid={`filter-category-${c.slug}`} />
    )) },
    !hide.includes("brand") && facets.brands.length > 0 && { key: "brand", title: t("brands"), body: facets.brands.map((b) => (
      <CheckRow key={b.slug} id={`f-brand-${b.slug}`} checked={csv(params.get("brand")).includes(b.slug)} onChange={() => toggle("brand", b.slug)} label={b.name} count={b.count} testid={`filter-brand-${b.slug}`} />
    )) },
    facets.sizes.length > 0 && { key: "size", title: t("size"), body: (
      <div className="flex flex-wrap gap-2 pt-1">{facets.sizes.map((s) => {
        const on = csv(params.get("size")).includes(s.value);
        return <button key={s.value} onClick={() => toggle("size", s.value)} aria-pressed={on} className={`min-w-11 h-10 px-3 border text-xs tabular transition-colors ${on ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground"}`} data-testid={`filter-size-${slug(s.value)}`}>{s.value}</button>;
      })}</div>) },
    facets.colors.length > 0 && { key: "color", title: t("colour"), body: facets.colors.map((c) => (
      <CheckRow key={c.value} id={`f-color-${slug(c.value)}`} checked={csv(params.get("color")).includes(c.value)} onChange={() => toggle("color", c.value)} label={c.value} count={c.count} testid={`filter-color-${slug(c.value)}`} />
    )) },
    facets.materials.length > 0 && { key: "material", title: t("material"), body: facets.materials.map((m) => (
      <CheckRow key={m.value} id={`f-mat-${slug(m.value)}`} checked={csv(params.get("material")).includes(m.value)} onChange={() => toggle("material", m.value)} label={m.value} count={m.count} testid={`filter-material-${slug(m.value)}`} />
    )) },
    { key: "price", title: `${t("price")} (ALL)`, body: (
      <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); setParam("min_price", pmin, "max_price", pmax); }}>
        {facets.price?.min && <p className="text-xs text-muted-foreground">{t("range")}: {formatALL(facets.price.min)} – {formatALL(facets.price.max)}</p>}
        <div className="flex items-center gap-2">
          <input inputMode="numeric" value={pmin} onChange={(e) => setPmin(e.target.value.replace(/[^0-9]/g, ""))} placeholder={`${t("min")} L`} className="w-full h-10 border px-3 text-sm bg-background" data-testid="plp-price-min-input" aria-label={t("min")} />
          <span className="text-muted-foreground">–</span>
          <input inputMode="numeric" value={pmax} onChange={(e) => setPmax(e.target.value.replace(/[^0-9]/g, ""))} placeholder={`${t("max")} L`} className="w-full h-10 border px-3 text-sm bg-background" data-testid="plp-price-max-input" aria-label={t("max")} />
        </div>
        <button className="text-[12px] uppercase tracking-[0.14em] font-semibold link-lux" data-testid="plp-price-apply-button">{t("apply")}</button>
      </form>) },
  ].filter(Boolean);

  return (
    <div data-testid="filter-panel">
      <div className="space-y-4 pb-5 border-b">
        <label className="flex items-center justify-between text-sm"><span>{t("in_stock_only")} <span className="text-muted-foreground tabular">({facets.in_stock_count})</span></span><Switch checked={params.get("availability") === "in_stock"} onCheckedChange={(v) => setParam("availability", v ? "in_stock" : "")} data-testid="filter-in-stock-switch" /></label>
        {!hide.includes("sale") && <label className="flex items-center justify-between text-sm"><span>{t("on_sale")} <span className="text-muted-foreground tabular">({facets.sale_count})</span></span><Switch checked={params.get("sale") === "true"} onCheckedChange={(v) => setParam("sale", v ? "true" : "")} data-testid="filter-sale-switch" /></label>}
      </div>
      <Accordion type="multiple" defaultValue={["gender", "category", "brand", "size"]}>
        {sections.map((s) => (
          <AccordionItem key={s.key} value={s.key}>
            <AccordionTrigger className="text-[12px] uppercase tracking-[0.14em] font-semibold hover:no-underline" data-testid={`filter-section-${s.key}`}>{s.title}</AccordionTrigger>
            <AccordionContent>{s.body}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}
